import { useEffect, useId, useRef, useState } from 'react';
import { MapPin, MapPinned } from 'lucide-react';
import { cargarGoogleMaps, mapsDisponible, type Lugar } from '@/lib/maps/googleMaps';
import { MapPickerDialog } from './MapPickerDialog';
import { useT } from '@/lib/i18n/useT';
import { cn } from '@/lib/cn';

interface Props {
  value: Lugar;
  onChange: (v: Lugar) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Acota las sugerencias a un país (`mx`, `es`…). Sin esto son mundiales. */
  country?: string;
  id?: string;
  className?: string;
}

/**
 * El campo de dirección de toda la aplicación.
 *
 * Un `<input>` de texto donde antes había un `<input>` de texto, pero con dos
 * cosas encima:
 *
 *  · **Autocompletado de Google**, para que la dirección que se guarda exista.
 *    Escrita a mano, «Av Reforma 100» y «Avenida Reforma #100, CDMX» son dos
 *    sitios distintos para cualquier consulta posterior, y ninguna de las dos
 *    se puede pintar en un mapa.
 *  · **Un mapa** para señalar el punto. Muchos eventos ocurren donde no hay
 *    portal —una explanada, un pabellón, la entrada norte de un recinto— y ahí
 *    la única forma honesta de decir dónde es señalarlo.
 *
 * ## Se puede seguir escribiendo a mano
 *
 * El autocompletado propone; no obliga. Un recinto recién inaugurado no está en
 * Google, y un campo que sólo acepta lo que Google conoce dejaría al usuario sin
 * poder guardar su propio evento. Al escribir libremente se conserva el texto y
 * se **borran las coordenadas**: lo que hay ya no es el sitio que se señaló, y
 * dejar el punto viejo pegado a una dirección nueva es peor que no tener punto.
 *
 * ## Sin clave de Google sigue siendo un campo de texto
 *
 * `VITE_GOOGLE_MAPS_API_KEY` vacía esconde el botón del mapa y desactiva las
 * sugerencias. No se rompe nada ni se promete algo que no va a pasar.
 */
export function AddressField({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  country,
  id,
  className,
}: Props) {
  const t = useT();
  const generado = useId();
  const inputId = id ?? generado;
  const inputRef = useRef<HTMLInputElement>(null);
  const [mapaAbierto, setMapaAbierto] = useState(false);
  const [sdkListo, setSdkListo] = useState(false);

  // `onChange` en una ref para que el efecto que engancha el autocompletado no
  // dependa de él: el padre suele pasar una función nueva en cada render, y con
  // ella en las dependencias el widget se destruiría y recrearía en cada tecla.
  const alCambiar = useRef(onChange);
  alCambiar.current = onChange;

  useEffect(() => {
    if (!mapsDisponible()) return;
    let vivo = true;
    cargarGoogleMaps()
      .then(() => vivo && setSdkListo(true))
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    if (!sdkListo || !inputRef.current || disabled) return;
    /*
      Cinturón, además del tirante.

      El cargador ya espera a que la biblioteca exista, pero esto corre dentro
      de un efecto: si algo saliera mal —una API sin habilitar, una versión que
      mueve la clase de sitio— la excepción sube al límite de error de React y
      **se lleva la pantalla por delante**. Un campo que no puede sugerir tiene
      que quedarse en un campo de texto, no en una página en blanco.
    */
    if (!window.google?.maps?.places?.Autocomplete) return;

    const auto = new google.maps.places.Autocomplete(inputRef.current, {
      // Sólo lo que se usa: cada campo de más se cobra aparte en la factura de
      // Google, y de un sitio aquí sólo hacen falta su nombre y su punto.
      fields: ['formatted_address', 'geometry.location', 'place_id', 'name'],
      ...(country ? { componentRestrictions: { country } } : {}),
    });

    const escucha = auto.addListener('place_changed', () => {
      const p = auto.getPlace();
      const loc = p.geometry?.location;
      if (!loc) return;
      alCambiar.current({
        // Muchos recintos tienen nombre propio y dirección aparte; se guardan
        // los dos porque «Palacio de los Deportes» sin la calle no llega a
        // nadie, y la calle sin el nombre no se reconoce.
        address: p.name && p.formatted_address?.startsWith(p.name)
          ? p.formatted_address
          : [p.name, p.formatted_address].filter(Boolean).join(' — '),
        latitude: loc.lat(),
        longitude: loc.lng(),
        placeId: p.place_id ?? null,
      });
    });

    return () => {
      escucha.remove();
      // El desplegable de sugerencias se cuelga del `<body>`, no del input, así
      // que al desmontar el campo se quedaría flotando sobre la pantalla.
      document.querySelectorAll('.pac-container').forEach((n) => n.remove());
    };
  }, [sdkListo, disabled, country]);

  const escribir = (texto: string) =>
    onChange(
      // Texto a mano = ya no es el punto que se señaló. Ver la nota de clase.
      value.latitude != null || value.longitude != null
        ? { address: texto, latitude: null, longitude: null, placeId: null }
        : { ...value, address: texto },
    );

  const tienePunto = value.latitude != null && value.longitude != null;

  return (
    <div className={className}>
      {label ? (
        <label
          htmlFor={inputId}
          className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]"
        >
          {label}
        </label>
      ) : null}

      <div className="flex items-stretch gap-1.5">
        <div className="relative min-w-0 flex-1">
          <MapPin
            aria-hidden
            className={cn(
              'pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2',
              // El icono se enciende cuando hay punto: es la única señal de que
              // esta dirección es una ubicación de verdad y no una cadena.
              tienePunto ? 'text-[var(--color-xk-accent)]' : 'text-[var(--color-xk-text-muted)]',
            )}
          />
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            value={value.address}
            onChange={(e) => escribir(e.target.value)}
            // El navegador propondría direcciones suyas encima de las de
            // Google, y las dos listas superpuestas no hay quien las use.
            autoComplete="off"
            disabled={disabled}
            placeholder={placeholder ?? t('address.placeholder')}
            className="w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] py-2.5 pl-8 pr-3 text-sm text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-accent-ink)] focus:outline-none disabled:opacity-50"
          />
        </div>

        {mapsDisponible() ? (
          <button
            type="button"
            onClick={() => setMapaAbierto(true)}
            disabled={disabled}
            aria-label={t('address.openMap')}
            title={t('address.openMap')}
            className={cn(
              'flex w-10 flex-none items-center justify-center rounded-lg border transition-colors',
              tienePunto
                ? 'border-[var(--color-accent-ink)] text-[var(--color-xk-accent)]'
                : 'border-[var(--color-xk-border)] text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]',
              'disabled:opacity-50',
            )}
          >
            <MapPinned className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {/* Qué se guardó exactamente. Sin esto, «con punto» y «sin punto» se ven
          igual y nadie sabe si el mapa llegó a registrar algo. */}
      <p className="mt-1 text-[10px] leading-snug text-[var(--color-xk-text-muted)]">
        {!mapsDisponible()
          ? t('address.noKey')
          : tienePunto
            ? t('address.pinned', {
                lat: value.latitude!.toFixed(5),
                lng: value.longitude!.toFixed(5),
              })
            : t('address.hint')}
      </p>

      {mapaAbierto ? (
        <MapPickerDialog
          value={value}
          onClose={() => setMapaAbierto(false)}
          onPick={(v) => {
            onChange(v);
            setMapaAbierto(false);
          }}
        />
      ) : null}
    </div>
  );
}
